import { useState, useRef, useEffect, useCallback } from "react";
import { AuthSignInSubPage, AuthMFASubtitle } from "../authBarStyling";
import { Auth } from "aws-amplify";
import ContinueBtn from "../../btn/continueBtn/continueBtn";
import { loginUser } from "../../../utils/auth/loginUser";
import { useRecoilState } from "recoil";
import { signInMenuActivatedSelector } from "../../../state/selectors/navbarToggle";
import CustomVerificationInput from "./verificationInput";
import { useToast } from "../../toast/ToastContext";

interface SignInVerificationPopupProps {
    username: string;
    mfaInvalid: boolean;
    setMfaInvalid: (mfaInvalid: boolean) => void;
    codeDeliveryDetails: { AttributeName?: string; DeliveryMedium?: string; Destination?: string };
    setCodeDeliveryDetails: (codeDeliveryDetails: { AttributeName?: string; DeliveryMedium?: string; Destination?: string }) => void;
    setPageName: (pageName: string) => void;
    pageName: string;
    countdown: number;
    activateCountdown: (active: boolean) => void;
    newPassword: string;
    setLoggingIn: (loggingIn: boolean) => void;
    resendNotReady: boolean;
    handleKeyboardDismiss: () => void;
}

const SignInVerificationPopup: React.FC<SignInVerificationPopupProps> = ({
    username,
    mfaInvalid,
    setMfaInvalid,
    codeDeliveryDetails,
    setCodeDeliveryDetails,
    setPageName,
    pageName,
    countdown,
    activateCountdown,
    newPassword,
    setLoggingIn,
    resendNotReady,
    handleKeyboardDismiss,
}) => {
    const resendOnOpenPopup = useRef(true);
    const [, setSignInMenuActivated] = useRecoilState(signInMenuActivatedSelector);
    const [isContinueBtnLoading, setIsContinueBtnLoading] = useState(false);
    const [mfaCode, setMfaCode] = useState("");
    const [, setResetAttemptsExceeded] = useState(false);
    const { showToast } = useToast();

    const forgotPasswordResend = useCallback(async () => {
        if (resendNotReady) return;
        activateCountdown(true);

        await Auth.forgotPassword(username)
            .then((data) => {
                setCodeDeliveryDetails(data.CodeDeliveryDetails);
            })
            .catch((err) => {
                if (err.code === "CodeMismatchException") {
                    setMfaInvalid(true);
                    showToast("You have entered the wrong code, please try again", "error");
                } else if (err.code === "LimitExceededException") {
                    setResetAttemptsExceeded(true);
                    showToast("You exceeded your reset attempts, please try again later", "error");
                }
            });
    }, [activateCountdown, resendNotReady, setCodeDeliveryDetails, setMfaInvalid, username, showToast]);

    const resetPasswordSubmit = async (mfaCode: string) => {
        setIsContinueBtnLoading(true);
        await Auth.forgotPasswordSubmit(username, mfaCode, newPassword)
            .then(async () => {
                await Auth.signIn(username, newPassword).then(async (user) => {
                    // let creds = await Auth.currentUserCredentials();
                    setIsContinueBtnLoading(false);
                    setLoggingIn(true);
                    await loginUser(user);
                    //back to default
                    setPageName("PhonePopup");
                    setLoggingIn(false);
                    //close auth
                    setSignInMenuActivated(false);
                });
            })
            .catch((err) => {
                setLoggingIn(false);
                setIsContinueBtnLoading(false);
                if (err.code === "CodeMismatchException") {
                    setMfaInvalid(true);
                    showToast("You have entered the wrong code, please try again", "error");
                } else if (err.code === "LimitExceededException") {
                    setResetAttemptsExceeded(true);
                    showToast("You exceeded your reset attempts, please try again later", "error");
                }
            });
    };

    useEffect(() => {
        if (resendNotReady) return;
        if (pageName === "SignInVerificationPopup" && resendOnOpenPopup.current) {
            forgotPasswordResend();
            resendOnOpenPopup.current = false;
        } else {
            if (pageName !== "SignInVerificationPopup") {
                resendOnOpenPopup.current = true;
            }
        }
    }, [forgotPasswordResend, pageName, resendNotReady]);

    if (pageName !== "SignInVerificationPopup") return null;
    const handleComplete = (code: string) => {
        if (code.length === 6) {
            resetPasswordSubmit(code);
        }
    };

    const handleChange = (code: string) => {
        setMfaInvalid(false);
        setMfaCode(code);
    };
    return (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AuthSignInSubPage style={{ height: "auto" }}>
                <CustomVerificationInput
                    value={mfaCode}
                    onChange={handleChange}
                    onComplete={handleComplete}
                    invalid={mfaInvalid}
                    onBlur={handleKeyboardDismiss}
                />
                <AuthMFASubtitle style={{ marginBottom: "24px" }}>{`We sent a 6 digit code to ${codeDeliveryDetails ? codeDeliveryDetails.Destination : ""}`}</AuthMFASubtitle>
                <ContinueBtn
                    style={{ marginTop: "0" }}
                    isLoading={isContinueBtnLoading}
                    background={"black"}
                    continueType="button"
                    onClick={forgotPasswordResend}
                    disabled={resendNotReady || isContinueBtnLoading}
                >
                    Resend code {!resendNotReady ? "" : `(${countdown})`}
                </ContinueBtn>
            </AuthSignInSubPage>
        </div>
    );
};

export default SignInVerificationPopup;
